<template>
  <div ref="el">
    <WideProductTile
      v-for="(product, index) in products"
      :key="index"
      :product-data="product"
      :index="index"
    />
  </div>
</template>

<script>
import { useIntersecting } from '@/composables/useIntersecting.js'
import { useListId } from '@/composables/useListId.js'
import { useCustomerStore } from '@/stores/customer'
import { mapState } from 'pinia'
import { defineAsyncComponent, ref } from 'vue'

const WideProductTile = defineAsyncComponent(
  () => import('@/components/pdp/organisms/WideProductTile.vue')
)

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: ApiController },
      { default: AnalyticsItemFactory },
      { default: MetaProduct },
      { default: GA4Controller }
    ] = await Promise.all([
      await import('@/services/ApiController'),
      await import('@/modules/tag_manager/AnalyticsItemFactory'),
      await import('@/modules/product/MetaProduct'),
      await import('@/services/analytics/GA4Controller')
    ])
    cache = {
      ApiController,
      AnalyticsItemFactory,
      MetaProduct,
      GA4Controller
    }
    return cache
  }
})()

export default {
  name: 'WideProductTileList',
  components: { WideProductTile },
  setup() {
    const el = ref(null)
    const isViewed = ref(false)

    useIntersecting(el, () => {
      isViewed.value = true
    })

    return { el, isViewed, ...useListId(el) }
  },
  props: {
    productsToFetch: [Array],
    isBundleForSingleProduct: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      analyticsItems: [],
      products: []
    }
  },
  computed: {
    ...mapState(useCustomerStore, ['customerIsAdmin'])
  },
  methods: {
    async onIsViewed() {
      const { GA4Controller } = await _getModules()

      void GA4Controller.pushViewItemListEvent({
        itemListId: this.itemListId,
        items: this.analyticsItems
      })
    },
    async fetchProductData() {
      const { AnalyticsItemFactory, ApiController } = await _getModules()

      this.products = await Promise.all(
        this.productsToFetch.map((product) =>
          ApiController.fetchMetaProduct(
            product.product_id,
            product.campaign_id
          )
        )
      )

      this.analyticsItems = this.products.map((item, index) =>
        AnalyticsItemFactory.getAnalyticsItemFromMetaProduct(
          item,
          this.itemListId,
          index
        )
      )
    }
  },
  watch: {
    isViewed(viewed) {
      if (viewed) this.onIsViewed()
    },
    productsToFetch: {
      handler: 'fetchProductData'
    }
  },
  // mounted, not created: created also runs during a server render, where it
  // would start a floating fetch whose result never makes it into the
  // response (VIP-12038)
  mounted() {
    this.fetchProductData()
  }
}
</script>
